import type { TableProps } from '@/lib/render'
import { Card } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

interface Props {
  props: unknown
}

export function TableRenderer({ props }: Props) {
  const data = props as TableProps

  if (!Array.isArray(data?.columns) || !Array.isArray(data?.rows)) {
    return (
      <div className="rounded-lg bg-destructive/10 p-4">
        <p className="text-[13px] text-destructive">Invalid table data</p>
      </div>
    )
  }

  return (
    <Card className="gap-0 overflow-hidden py-0">
      {data.title ? (
        <p className="border-b border-border bg-muted px-4 py-2.5 text-sm font-semibold text-foreground">
          {data.title}
        </p>
      ) : null}

      <div className="overflow-x-auto">
        <Table>
          {/* Header */}
          <TableHeader>
            <TableRow className="bg-muted">
              {data.columns.map((col, i) => (
                <TableHead
                  key={i}
                  className="min-w-[100px] max-w-[200px] border-r border-muted px-3 py-2 text-xs font-bold uppercase tracking-wide text-muted-foreground"
                >
                  {col}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          {/* Rows */}
          <TableBody>
            {data.rows.map((row, ri) => (
              <TableRow key={ri} className={ri % 2 === 0 ? 'bg-card' : 'bg-muted/50'}>
                {row.map((cell, ci) => (
                  <TableCell
                    key={ci}
                    className="min-w-[100px] max-w-[200px] whitespace-normal border-r border-muted px-3 py-2 align-top text-[13px] text-foreground"
                  >
                    <span className="line-clamp-2">{cell === null ? '—' : String(cell)}</span>
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {data.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={data.columns.length} className="p-6 text-center text-sm text-muted-foreground">
                  No results
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>

      <p className="border-t border-border bg-muted px-3 py-1.5 text-[11px] text-muted-foreground">
        {data.rows.length} row{data.rows.length !== 1 ? 's' : ''}
      </p>
    </Card>
  )
}
